/**
 * HiringDashboard — primary shell for recruiters & institutions.
 * Auto-bootstraps the hiring profile via /api/marketplace/hiring/me on first visit.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/Skeleton";
import {
  Briefcase, Search, Users, FileText, Inbox, Plus, Crown,
  Building2, Sparkles, ArrowRight, Loader2, Eye, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import PlanUpgradeSection from "@/components/PlanUpgradeSection";

export default function HiringDashboard() {
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [stats, setStats] = useState({ jobs: 0, requests: 0, applications: 0, saved: 0 });
  const [enabling, setEnabling] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        await api.post("/roles/switch", { role: "hiring", enable_hiring: true }).catch(() => {});
        const [o, jobs, reqs] = await Promise.all([
          api.get("/marketplace/hiring/me"),
          api.get("/marketplace/jobs/mine"),
          api.get("/marketplace/cv-requests/outgoing"),
        ]);
        setOrg(o.data);
        setStats({
          jobs: jobs.data.length,
          requests: reqs.data.length,
          applications: 0,
          saved: 0,
        });
      } catch (err) {
        toast.error("Could not load hiring dashboard");
      }
    })();
  }, []);

  if (!org) {
    return (
      <Layout title="Hiring Hub">
        <Skeleton className="h-72 rounded-3xl" />
      </Layout>
    );
  }

  return (
    <Layout title="Hiring Hub">
      {/* Hero */}
      <section className="tb-card-feature relative overflow-hidden" data-testid="hiring-hero">
        <div className="absolute inset-0 tb-grid-bg opacity-50 pointer-events-none" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <span className="tb-chip-lavender inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide">
              <Briefcase className="h-3 w-3" /> TeeBee Educator Marketplace · Hiring
            </span>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl tracking-tight font-medium leading-tight">
              Discover educators ready for your classrooms
            </h1>
            <p className="mt-2 text-sm text-muted-foreground max-w-xl">
              Search verified teachers across curricular and extra-curricular categories worldwide. Respect their privacy — view only the fields they permit.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={() => navigate("/hiring/discover")} className="rounded-full" data-testid="hiring-discover-btn">
                <Search className="h-3.5 w-3.5 mr-1.5" /> Discover educators
              </Button>
              <Button onClick={() => navigate("/hiring/jobs/new")} variant="outline" className="rounded-full" data-testid="hiring-new-job-btn">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> Post a job
              </Button>
              <Button onClick={() => navigate("/hiring/requests")} variant="outline" className="rounded-full" data-testid="hiring-requests-btn">
                <Inbox className="h-3.5 w-3.5 mr-1.5" /> CV requests
              </Button>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold text-muted-foreground">Hiring org</p>
            <p className="font-display text-xl font-medium tracking-tight">{org.org_name}</p>
            {org.location && <p className="text-[11px] text-muted-foreground">{org.location}</p>}
            <Button size="sm" variant="ghost" className="rounded-full mt-2" onClick={() => navigate("/hiring/profile")} data-testid="hiring-edit-org">
              Edit institution <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      </section>

      {/* KPI */}
      <section className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 tb-stagger" data-testid="hiring-kpis">
        <Kpi icon={FileText} chip="tb-chip-lavender" label="Job posts" value={stats.jobs}        onClick={() => navigate("/hiring/jobs")} />
        <Kpi icon={Inbox}    chip="tb-chip-sky"      label="CV requests" value={stats.requests}  onClick={() => navigate("/hiring/requests")} />
        <Kpi icon={Users}    chip="tb-chip-mint"     label="Applications" value={stats.applications} />
        <Kpi icon={Eye}      chip="tb-chip-butter"   label="Saved candidates" value={stats.saved} />
      </section>

      {/* Tiles */}
      <section className="mt-6">
        <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-muted-foreground inline-flex items-center gap-2 mb-3">
          <Sparkles className="h-3 w-3" /> What you can do
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3 tb-stagger">
          <Tile icon={Search}    chip="tb-chip-lavender" title="Discover educators"  hint="Search verified teachers across all categories"   to="/hiring/discover" testid="hiring-tile-discover" />
          <Tile icon={Plus}      chip="tb-chip-sky"      title="Post a job"           hint="List a role and receive applications via TeeBee" to="/hiring/jobs/new" testid="hiring-tile-newjob" />
          <Tile icon={Inbox}     chip="tb-chip-butter"   title="Pipeline"             hint="Kanban view of all applications by stage"        to="/hiring/applications" testid="hiring-tile-pipeline" />
          <Tile icon={Users}     chip="tb-chip-mint"     title="Team"                 hint="Invite recruiters and set roles"                  to="/hiring/team" testid="hiring-tile-team" />
          <Tile icon={Building2} chip="tb-chip-lavender" title="Edit institution"     hint="Logo, website, location, hiring story"           to="/hiring/profile" testid="hiring-tile-org" />
        </div>
      </section>

      <PlanUpgradeSection source="hiring" id="plans" />
    </Layout>
  );
}

function Kpi({ icon: Icon, chip, label, value, onClick }) {
  return (
    <button onClick={onClick} className="tb-card-interactive p-4 text-left" data-testid={`hiring-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className={`h-9 w-9 rounded-xl ${chip} grid place-items-center`}>
        <Icon className="h-4 w-4" />
      </span>
      <p className="mt-3 font-display text-2xl tracking-tight font-medium">{value}</p>
      <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
    </button>
  );
}

function Tile({ icon: Icon, chip, title, hint, to, testid }) {
  const navigate = useNavigate();
  return (
    <button onClick={() => navigate(to)} className="tb-card-interactive p-4 text-left group" data-testid={testid}>
      <span className={`inline-grid place-items-center h-9 w-9 rounded-xl ${chip}`}>
        <Icon className="h-4 w-4" />
      </span>
      <p className="mt-3 text-sm font-display font-medium tracking-tight leading-snug">{title}</p>
      <p className="mt-1 text-[11px] text-muted-foreground line-clamp-2">{hint}</p>
      <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-primary">
        Open <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
      </span>
    </button>
  );
}
